import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createOrderSession,
  getOrderSession,
  setUserInfo,
  bookSeats,
  processPayment,
  claimGuestSession,
  getUserOrderById,
  getOrderPayment,
} from "../../../api/services/orders/orders.service";
import type {
  CreateSessionRequest,
  SetUserInfoRequest,
  BookSeatsRequest,
  ProcessPaymentRequest,
  ClaimGuestSessionRequest,
} from "../../../api/services/orders/types";
import { eventsService } from "../../../api/services/events/events.service";
import { addBalance, getUserBalance } from "../../../api/services/payment";
import type {
  AddBalanceRequest,
  AddBalanceResponse,
} from "../../../api/services/payment/types";

// Create order session
export function useCreateOrderSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSessionRequest) => createOrderSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-sessions"] });
    },
  });
}

// Get a specific user order by ID (supports optional polling)
export function useGetUserOrder(
  orderId: string,
  enabled: boolean = true,
  refetchInterval?: number | false
) {
  return useQuery({
    queryKey: ['user-order', orderId],
    queryFn: () => getUserOrderById(orderId),
    enabled: enabled && !!orderId,
    staleTime: 5000,
    refetchInterval,
  });
}

// Get order payment details from payment provider
export function useGetOrderPayment(
  orderId: string,
  enabled: boolean = true,
  refetchInterval?: number | false
) {
  return useQuery({
    queryKey: ['order-payment', orderId],
    queryFn: () => getOrderPayment(orderId),
    enabled: enabled && !!orderId,
    staleTime: 5000,
    refetchInterval,
  });
}

// Get order session details
export function useGetOrderSession(sessionId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ["order-session", sessionId],
    queryFn: () => getOrderSession(sessionId),
    enabled: enabled && !!sessionId,
    staleTime: 30000, // 30 seconds
  });
}

// Set user information for order
export function useSetUserInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetUserInfoRequest) => setUserInfo(data),
    onSuccess: (_, variables) => {
      // Invalidate the specific order session
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
    },
  });
}

// Book seats for order
export function useBookSeats() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BookSeatsRequest) => bookSeats(data),
    onSuccess: (data, variables) => {
      // Invalidate the specific order session
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
      
      // Invalidate seats availability for the schedule
      const scheduleId = data.session?.metadata?.scheduleId;
      if (scheduleId) {
        queryClient.invalidateQueries({
          queryKey: ['schedule-seats', scheduleId],
        });
      }
    },
  });
}

// Process payment for order
export function useProcessPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProcessPaymentRequest) => processPayment(data),
    onSuccess: () => {
      // Invalidate user orders after successful payment
      queryClient.invalidateQueries({ queryKey: ["user-orders"] });
      queryClient.invalidateQueries({ queryKey: ["order-sessions"] });
    },
  });
}

// Claim guest session after login
export function useClaimGuestSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClaimGuestSessionRequest) => claimGuestSession(data),
    onSuccess: (_, variables) => {
      // Invalidate the specific order session and sessions list
      queryClient.invalidateQueries({
        queryKey: ["order-session", variables.sessionId],
      });
      queryClient.invalidateQueries({ queryKey: ["order-sessions"] });
    },
  });
}

// Add balance to user account (top-up)
export function useAddBalance() {
  const queryClient = useQueryClient();

  return useMutation<AddBalanceResponse, Error, AddBalanceRequest>({
    mutationFn: (data: AddBalanceRequest) => addBalance(data),
    onSuccess: () => {
      // Invalidate user balance so UI updates after top-up
      queryClient.invalidateQueries({ queryKey: ["user-balance"] });
    },
  });
}

// Get authenticated user's payment balance
export function useGetUserBalance(enabled: boolean = true) {
  return useQuery({
    queryKey: ["user-balance"],
    queryFn: () => getUserBalance(),
    enabled,
    staleTime: 30_000, // 30s
  });
}

// Get schedule details by ID
export function useGetSchedule(scheduleId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['schedule', scheduleId],
    queryFn: () => eventsService.getScheduleById(scheduleId),
    enabled: enabled && !!scheduleId,
    staleTime: 60000, // 1 minute
  });
}

// Get schedule seating map SVG
export function useGetScheduleMapSvg(scheduleId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['schedule-map', scheduleId],
    queryFn: () => eventsService.getScheduleMapSvg(scheduleId),
    enabled: enabled && !!scheduleId,
    staleTime: 300000, // 5 minutes - SVG rarely changes
    gcTime: 600000, // 10 minutes - keep cached longer
  });
}

// Get schedule seats with availability status
export function useGetScheduleSeats(scheduleId: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['schedule-seats', scheduleId],
    queryFn: () => eventsService.getScheduleSeats(scheduleId),
    enabled: enabled && !!scheduleId,
    staleTime: 10000, // 10 seconds - seats availability changes frequently
    refetchInterval: 15000, // Auto-refetch every 15 seconds
  });
}
